import { BOMCalculationInput, BOMCalculationResult, PanelRow, calculateBOM } from './bom.utils';
import { PanelOrientation } from '../types';

/**
 * Input data specific to LGCY BOM calculations.
 * Extends the base BOM input with LGCY-specific fields.
 */
export interface LgcyBomInput {
  /** Base BOM calculation input (structures, module dimensions) */
  bomInput: BOMCalculationInput;
  /** Total number of panel rows across all structures */
  totalRowsOfPanels: number;
  /** Free-text arrays count entered by user */
  arrays: number;
  /** Total number of defined strings/circuits across all systems */
  stringsCircuits: number;
  /** RSD quantity for Tesla, 0 otherwise */
  mciS: number;
  /** Total number of modules (panels) across all systems */
  totalModules: number;
  /** Whether the inverter brand is Enphase */
  isEnphase: boolean;
  /** LGCY BOM offset percentage (from settings, default 5) */
  offsetPercent: number;
}

/**
 * Complete LGCY BOM calculation result.
 */
export interface LgcyBomResult {
  /** Sum of all panels across systems */
  sumOfPanels: number;
  /** Total rails (from base BOM, with offset instead of safety factor) */
  railsInTotal: number;
  /** End caps = end clamps from base BOM */
  endCap: number;
  /** Splice kits (from base BOM, with offset instead of safety factor) */
  spliceKit: number;
  /** Mid clamps total (from base BOM) */
  midClampsTotal: number;
  /** End clamps total (from base BOM) */
  endClampsTotal: number;
  /** Washer-Lag 11632: 1 per attachment (LAG 11634 / 11637 spec) */
  washerLag11632: number;
  /** Estimated attachments count */
  estimatedAttachments: number;
  /** Universal wire clamp: 1 per row */
  universalWireClamp: number;
  /** Wire retention clip: 2 per module + 1 per array */
  wireRetentionClip: number;
  /** Junction box: 1 per array */
  junctionBox: number;
  /** Grounding lugs: rows with panels * 2 */
  groundingLugs: number;
  /** Enphase-specific items (null if not Enphase) */
  enphase: EnphaseBomResult | null;
}

/**
 * Enphase-specific BOM items.
 */
export interface EnphaseBomResult {
  /** 11242 Q Cable Landscape (60 Cell): 1 per module */
  qCableLandscape: number;
  /** 11230 Q Cable Female Connector */
  qCableFemaleConnector: number;
  /** 11250 Q Cable Male Connector */
  qCableMaleConnector: number;
  /** 11270 Q Cable Sealing Cap: 1 per string */
  qCableSealingCap: number;
  /** 11280 Q Cable Terminator Cap: 1 per string */
  qCableTerminatorCap: number;
}

/**
 * Applies the LGCY offset percentage (roundUp(+N%)) to a value.
 * @param value - Raw calculated value
 * @param offsetPercent - Offset percentage (e.g. 5 for 5%)
 * @returns Value with offset applied, rounded up
 */
const applyOffset = (value: number, offsetPercent: number): number => {
  if (value === 0) return 0;
  return Math.ceil(value * (1 + offsetPercent / 100));
};

/**
 * Calculates the highest of three values, each divided by 10 and rounded up.
 * Used for Enphase Q Cable connectors.
 */
const highestOfThreeDivTen = (arrays: number, strings: number, modules: number): number => {
  return Math.max(
    Math.ceil(arrays / 10),
    Math.ceil(strings / 10),
    Math.ceil(modules / 10)
  );
};

/**
 * Calculates the complete LGCY BOM.
 * Uses base BOM calculation (with safetyFactor=0 since LGCY uses offset instead)
 * then adds LGCY-specific items and applies offset.
 */
export const calculateLgcyBom = (input: LgcyBomInput): LgcyBomResult => {
  // Calculate base BOM with safetyFactor=0 (LGCY uses offset instead)
  const baseBom: BOMCalculationResult = calculateBOM({
    ...input.bomInput,
    safetyFactor: 0,
  });

  const {
    totalRowsOfPanels,
    arrays,
    stringsCircuits,
    mciS,
    totalModules,
    isEnphase,
    offsetPercent,
  } = input;

  // Rails & Splice Kit — apply offset to raw (unrounded) sums per Excel formula
  const railsInTotal = applyOffset(baseBom.rawTotalRails, offsetPercent);
  const spliceKit = Math.max(2, applyOffset(baseBom.rawTotalSpliceKits, offsetPercent));
  // Mid Clamps, End Clamps — no offset
  const midClampsTotal = baseBom.totalMidClamps;
  const endClampsTotal = baseBom.totalEndClamps;

  // Washer-Lag is manual-entry only (not auto-calculated)

  // Estimated attachments: LANDSCAPE → ceil(panel_count × 4), PORTRAIT → ceil(panel_count × 2.5)
  const estimatedAttachments = input.bomInput.structures.reduce((sum, structure) => {
    const allRows: PanelRow[] = [
      ...(structure.panel_rows_1?.rows || []),
      ...(structure.panel_rows_2?.rows || []),
    ];
    return sum + allRows.reduce((rowSum, row) => {
      if (row.panel_count <= 0) return rowSum;
      const multiplier = row.orientation === PanelOrientation.LANDSCAPE ? 4 : 2.5;
      return rowSum + Math.ceil(row.panel_count * multiplier);
    }, 0);
  }, 0);

  // Universal wire clamp: 1 per row (no offset)
  const universalWireClamp = totalRowsOfPanels;

  // Wire retention clip: (2 * total panels) + arrays (no offset)
  const wireRetentionClip = (totalModules * 2) + arrays;

  // Junction box: 1 per array (no offset)
  const junctionBox = arrays;

  // Grounding lugs: rows with panels (no offset)
  const groundingLugs = totalRowsOfPanels;

  // End cap matches end clamps
  const endCap = endClampsTotal;

  // Enphase-specific items
  let enphase: EnphaseBomResult | null = null;
  if (isEnphase) {
    const qCableLandscape = applyOffset(totalModules, offsetPercent);
    const connectorCount = highestOfThreeDivTen(arrays, stringsCircuits, totalModules);
    const qCableFemaleConnector = applyOffset(connectorCount, offsetPercent);
    const qCableMaleConnector = applyOffset(connectorCount, offsetPercent);
    const qCableSealingCap = applyOffset(stringsCircuits, offsetPercent);
    const qCableTerminatorCap = applyOffset(stringsCircuits, offsetPercent);

    enphase = {
      qCableLandscape,
      qCableFemaleConnector,
      qCableMaleConnector,
      qCableSealingCap,
      qCableTerminatorCap,
    };
  }

  return {
    sumOfPanels: totalModules,
    railsInTotal,
    endCap,
    spliceKit,
    midClampsTotal,
    endClampsTotal,
    washerLag11632: 0,
    estimatedAttachments,
    universalWireClamp,
    wireRetentionClip,
    junctionBox,
    groundingLugs,
    enphase,
  };
};
